import { Sheet, useBreakpointValue } from '@hover/blueprint';
import { assertUnreachable } from '@hover/utilities';
import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router';

import { DownloadApp } from 'src/modules/homeownerApp/components/DownloadApp';
import { ScanLearnMore } from 'src/modules/homeownerApp/components/ScanLearnMore/ScanLearnMore';
import type { HomeownerAppMenuNamesValues } from 'src/modules/homeownerApp/providers/HomeownerAppProvider';
import { HomeownerAppMenuNames } from 'src/modules/homeownerApp/providers/HomeownerAppProvider';
import { useIsEmbed } from 'src/shared/hooks/useIsEmbed';

import { useHomeownerAppContext } from '../../hooks/useHomeownerAppContext';
import { useSaveToProjectContext } from '../../providers/SaveToProjectProvider';
import { MoreActions } from '../OriginalImageScreen/MoreActions';
import { ProfileSheet } from '../ProfileSheet';
import { AddIdeasToProject } from '../Projects/Menu/AddIdeasToProject';
import { AddSavedToProject } from '../Projects/Menu/AddSavedToProject';
import { AddToProject } from '../Projects/Menu/AddToProject';
import { EditProject } from '../Projects/Menu/EditProject';
import { ImageCollection } from '../Projects/Menu/ImageCollection';
import { ProjectMoreActions } from '../Projects/Menu/MoreActions';
import { NewProject } from '../Projects/Menu/NewProject';
import { WelcomeSheet } from '../WelcomeSheet';

import { SavedImageSheet } from './SavedImageSheet';
import { SignInSheet } from './SignInSheet';
import { UploadHomeownerPhotoSheet } from './UploadHomeownerPhotoSheet';

const bottomVariantSheetMenus = new Set<HomeownerAppMenuNamesValues>([
  HomeownerAppMenuNames.UPLOAD_PHOTO,
  HomeownerAppMenuNames.ADD_IDEAS_TO_PROJECT,
  HomeownerAppMenuNames.ADD_SAVED_TO_PROJECT,
  HomeownerAppMenuNames.IMAGE_COLLECTION,
  HomeownerAppMenuNames.SAVED_IMAGES,
]);

const modalVariantSheetMenus = new Set<HomeownerAppMenuNamesValues>([
  HomeownerAppMenuNames.START_SCAN_LEARN_MORE,
  HomeownerAppMenuNames.DOWNLOAD_APP,
]);

const mobileMenusWithCloseButton = new Set<HomeownerAppMenuNamesValues>([
  HomeownerAppMenuNames.ADD_TO_PROJECT,
  HomeownerAppMenuNames.UPLOAD_PHOTO,
  HomeownerAppMenuNames.PROFILE,
  HomeownerAppMenuNames.SIGN_IN,
  HomeownerAppMenuNames.WELCOME_TO,
  HomeownerAppMenuNames.WELCOME_BACK,
  HomeownerAppMenuNames.ADD_SAVED_TO_PROJECT,
  HomeownerAppMenuNames.ADD_IDEAS_TO_PROJECT,
  HomeownerAppMenuNames.IMAGE_COLLECTION,
  HomeownerAppMenuNames.SAVED_IMAGES,
  HomeownerAppMenuNames.START_SCAN_LEARN_MORE,
  HomeownerAppMenuNames.DOWNLOAD_APP,
]);

const fullSizeMobileMenus = new Set<HomeownerAppMenuNamesValues>([
  HomeownerAppMenuNames.NEW_PROJECT,
  HomeownerAppMenuNames.EDIT_PROJECT,
  HomeownerAppMenuNames.SAVED_IMAGES,
  HomeownerAppMenuNames.START_SCAN_LEARN_MORE,
  HomeownerAppMenuNames.DOWNLOAD_APP,
]);

type SheetVariants = 'modal' | 'bottom' | undefined;

const getVariant = (menu: HomeownerAppMenuNamesValues | null) => {
  if (!menu) return undefined;

  if (modalVariantSheetMenus.has(menu)) {
    return 'modal';
  }
  if (bottomVariantSheetMenus.has(menu)) {
    return 'bottom';
  }

  return undefined;
};

const getMenuContent = (menu: HomeownerAppMenuNamesValues | null) => {
  switch (menu) {
    case HomeownerAppMenuNames.ORIGINAL_IMAGE_MORE_ACTIONS:
      return <MoreActions />;
    case HomeownerAppMenuNames.PROFILE:
      return <ProfileSheet />;
    case HomeownerAppMenuNames.PROJECT_MORE_ACTIONS:
      return <ProjectMoreActions />;
    case HomeownerAppMenuNames.SIGN_IN:
      return <SignInSheet />;
    case HomeownerAppMenuNames.UPLOAD_PHOTO:
      return (
        <Sheet.Contents>
          <UploadHomeownerPhotoSheet
            marginBottom={{ base: undefined, tablet: 800 }}
            marginTop={{ base: 800, tablet: 600 }}
            px={{ base: 500, tablet: 600 }}
          />
        </Sheet.Contents>
      );
    case HomeownerAppMenuNames.WELCOME_BACK:
      return <WelcomeSheet />;
    case HomeownerAppMenuNames.WELCOME_TO:
      return <WelcomeSheet isSigningUp />;
    case HomeownerAppMenuNames.ADD_TO_PROJECT:
      return <AddToProject />;
    case HomeownerAppMenuNames.NEW_PROJECT:
      return <NewProject />;
    case HomeownerAppMenuNames.EDIT_PROJECT:
      return <EditProject />;
    case HomeownerAppMenuNames.ADD_SAVED_TO_PROJECT:
      return <AddSavedToProject />;
    case HomeownerAppMenuNames.ADD_IDEAS_TO_PROJECT:
      return <AddIdeasToProject />;
    case HomeownerAppMenuNames.IMAGE_COLLECTION:
      return <ImageCollection />;
    case HomeownerAppMenuNames.SAVED_IMAGES:
      return <SavedImageSheet />;
    case HomeownerAppMenuNames.START_SCAN_LEARN_MORE:
      return <ScanLearnMore />;
    case HomeownerAppMenuNames.DOWNLOAD_APP:
      return <DownloadApp />;
    case null:
      return null;
    default:
      return assertUnreachable(menu);
  }
};

const SheetMenu = () => {
  const { currentMenu, setCurrentMenu } = useHomeownerAppContext();
  const { setImageToSave } = useSaveToProjectContext();
  const history = useHistory();
  const isMobile = useBreakpointValue({
    base: true,
    tablet: false,
  });

  const [variant, setVariant] = useState<SheetVariants>(undefined);

  const isModal = !!currentMenu && modalVariantSheetMenus.has(currentMenu);
  const isFullSizeMobile =
    !!currentMenu && fullSizeMobileMenus.has(currentMenu);
  const isClosable = useBreakpointValue({
    base: !!currentMenu && mobileMenusWithCloseButton.has(currentMenu),
    tablet: true,
  });
  const mobileSize = isFullSizeMobile ? 'full' : 'auto';

  useEffect(() => {
    if (currentMenu) {
      setVariant(getVariant(currentMenu));
    }
  }, [currentMenu]);

  const isEmbed = useIsEmbed();
  const { search } = useLocation();
  const modalContentProps = isModal
    ? {
        sx: { padding: '0 !important' },
        top: { base: isFullSizeMobile ? 0 : 'unset', tablet: 'unset' },
      }
    : {};

  const profileSheetProps =
    currentMenu === HomeownerAppMenuNames.PROFILE
      ? {
          sx: { padding: '0 !important' },
        }
      : {};

  const needBottomPadding =
    isMobile && currentMenu === HomeownerAppMenuNames.SAVED_IMAGES
      ? {
          sx: { paddingBottom: '96px !important' },
        }
      : {};

  return (
    <Sheet.Container
      autoFocus={isEmbed ? false : undefined}
      blockScrollOnMount={isEmbed ? false : undefined}
      contentProps={{
        ...modalContentProps,
        ...profileSheetProps,
        ...needBottomPadding,
      }}
      isClosable={isClosable}
      isOpen={!!currentMenu}
      onClose={() => {
        const queryParams = new URLSearchParams(search);
        queryParams.delete('login');
        setCurrentMenu(null);
        setImageToSave(undefined);
        history.replace({
          pathname: location.pathname,
          search: queryParams.toString(),
          state: undefined,
        });
      }}
      onCloseComplete={() => setVariant(undefined)}
      scrollBehavior="inside"
      size={{ base: mobileSize, tablet: 'auto' }}
      variant={variant}
    >
      {getMenuContent(currentMenu)}
    </Sheet.Container>
  );
};

export { SheetMenu };
